import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Spinner } from '@fluxora/ui';

import { useAgreementPreviewQuery } from '../api/agreement.queries';
import { isNotImplemented, problemFieldsOf } from '../api/bff-client';
import { useFunnelSnapshotQuery } from '../api/funnel.queries';
import { useSignAcceptMutation, useSignContextQuery } from '../api/sign.queries';
import { useFunnelCandidateId } from '../hooks/useFunnelCandidateId';
import { useSignOperation } from '../hooks/useSignOperation';
import { useUsageSelection } from '../hooks/useUsageSelection';
import { buildPlanStepPath } from '../services/funnel-rehydrate.service';
import { FunnelBackLink } from '../components/FunnelBackLink';
import { ClickwrapConsent } from '../components/ClickwrapConsent';
import { ContractPreview } from '../components/ContractPreview';
import { EflDisclosure } from '../components/EflDisclosure';
import { RescissionDisclosure } from '../components/RescissionDisclosure';
import {
  SignCompletionPanel,
  SignCompletionUnlinked,
} from '../components/SignCompletionPanel';
import { SignDepositGate } from '../components/SignDepositGate';
import { SignaturePad } from '../components/SignaturePad';
import { SigningPanel } from '../components/SigningPanel';
import { customerSafeDetail } from '../services/bff-detail.service';
import {
  isSignActionable,
  signBlockedReasonKey,
} from '../services/agreement-preview.service';
import { resolveSignStepView } from '../services/sign-completion.service';
import {
  buildSignatureEvent,
  emptySignForm,
  hasDrawnSignature,
  isConsentComplete,
  isDepositGateSatisfied,
  withDrawnSignature,
  type SignForm,
  type SignFormErrors,
} from '../services/sign.service';
import { env } from '../../../config/env';
import type { SignContextResponse } from '@rheora/enrollment-contract';

/**
 * Review the contract, sign it, save it (E-SF06 + E-SF18).
 *
 * Reached at `/plan/:slug/sign?candidate=<candidateId>` after the deposit decision
 * (E-SF05). The step is built around the DOCUMENT:
 *
 *     RENDER  the real unsigned contract PDF, inline and scrollable
 *     REVIEW  the customer reads what they are about to execute
 *     SIGN    typed name (captured at credit) + the three acknowledgements,
 *             optionally with a drawn mark, presented with that document
 *     SAVE    the server composites the mark into the AGREEMENT and stores it
 *     CONFIRM the executed document is shown and downloadable
 *
 * ── ⚠️ THE LAYOUT IS LOAD-BEARING, NOT DECORATION (rebuilt 2026-08-26) ──────
 * This step used to be a single `max-w-2xl` column, and three separate defects fell
 * out of that one choice. Measured in a real browser at 1375×1750:
 *
 *   - the page container was 672px, so a legal contract rendered in a 493px-wide
 *     `<object>` with ~700px of empty page either side;
 *   - the signature canvas sat at y≈3652 on a 4,263px page — roughly 2,900px BELOW
 *     the contract it accompanies, behind the EFL, payment and deposit blocks;
 *   - "read the contract, then sign it" meant scrolling a document out of sight and
 *     then hunting for a pad that looked like every other bordered box.
 *
 * So on desktop the document gets its OWN full-height reading column, made sticky,
 * and everything the customer must DO — start date, disclosures, acknowledgements,
 * deposit, and the signing panel — runs down a rail beside it. The contract stays on
 * screen for the whole of the signing act, which collapses the review→sign distance
 * to zero without moving the signature pad off the end of the flow (the customer
 * asked for it to stay there). Below `lg` it degrades to the original single column,
 * in the original order.
 *
 * ⚠️ AND IT IS NOT A `<main>`. `StorefrontLayout` already renders one; a second
 * `main` landmark inside it is an a11y defect the flat layout carried.
 *
 * ── FAIL LOUD, NEVER FAKE A SIGN ───────────────────────────────────────────
 * The `/bff/sign/*` routes are owned by the at-accept saga (built separately). Until
 * they land they 404/501; the page shows an HONEST "sign isn't available yet"
 * pending state and never pretends the contract was signed (US posture; ADR-0009).
 *
 * All five async states are handled: no-candidate (guard), loading (Spinner),
 * error (Alert, incl. the honest not-available), the loaded sign surface, and the
 * signed confirmation.
 */
export function SignPage(): JSX.Element {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const candidateId = useFunnelCandidateId();
  // The chosen usage rides every hop of the funnel (see funnel-rehydrate.service),
  // so stepping back to the estimate does not drop it.
  const selection = useUsageSelection();

  const context = useSignContextQuery(candidateId);
  const accept = useSignAcceptMutation();

  // ⚠️ SERVER TRUTH ABOUT WHETHER THIS STEP IS ALREADY FINISHED.
  //
  // The confirmation used to exist ONLY as `accept.isSuccess` — browser memory. A
  // refresh of this URL after signing therefore re-rendered the entire sign surface
  // WITH A LIVE "Sign and enroll" BUTTON, in front of a customer whose contract was
  // already executed (reproduced 2026-08-26). `GET /bff/funnel` answers this
  // durably, per candidate, and now carries the `statusToken` too — so completion is
  // RE-DERIVED rather than remembered, which is the replay-safe posture the rest of
  // this app already holds itself to.
  //
  // Reading the snapshot here adds NO request: `FunnelHydrationGate` already
  // observes this key with `staleTime: Infinity`, so this is a second observer on
  // one cache entry.
  const snapshot = useFunnelSnapshotQuery();

  // No resolved meter → send the shopper to the landing chooser to resolve it
  // first (SHOP-6).
  if (candidateId === undefined) {
    return <Navigate to="/" replace />;
  }

  const view = resolveSignStepView({
    acceptResult: accept.isSuccess ? accept.data : undefined,
    snapshotSigned: snapshot.data?.signed,
    snapshotStatusToken: snapshot.data?.statusToken,
  });

  const backLink = (
    <FunnelBackLink
      to={
        slug === undefined
          ? '/'
          : buildPlanStepPath({ tariffId: slug, step: 'estimate', selection })
      }
      label={t('shop.sign.back')}
    />
  );

  // ── COMPLETED ── the enrollment is signed. Nothing else on this step applies,
  // and re-rendering the sign surface behind a confirmation would be an invitation
  // to sign twice.
  if (view.kind === 'signed') {
    return (
      <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-10">
        <SignCompletionPanel
          statusToken={view.statusToken}
          serviceStartDate={view.serviceStartDate}
          rescissionDeadline={view.rescissionDeadline}

        />
      </div>
    );
  }
  if (view.kind === 'signed-unlinked') {
    return (
      <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-10">
        <SignCompletionUnlinked />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-10">
      {backLink}
      <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
        {t('shop.sign.heading')}
      </h1>

      {context.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" label={t('shop.sign.loading')} />
        </div>
      ) : null}

      {context.isError ? (
        <Alert
          variant={isNotImplemented(context.error) ? 'warning' : 'danger'}
          title={t('shop.sign.error.title')}
          role={isNotImplemented(context.error) ? 'status' : 'alert'}
        >
          {isNotImplemented(context.error)
            ? t('shop.sign.error.notAvailable')
            : t('shop.sign.error.body')}
        </Alert>
      ) : null}

      {context.isSuccess ? (
        <SignSurface
          candidateId={candidateId}
          slug={slug}
          context={context.data}
          accept={accept}
        />
      ) : null}
    </div>
  );
}

interface SignSurfaceProps {
  readonly candidateId: string;
  readonly slug: string | undefined;
  readonly context: SignContextResponse;
  readonly accept: ReturnType<typeof useSignAcceptMutation>;
}

/** The loaded sign surface — split out so the form state mounts only with a context. */
function SignSurface({ candidateId, slug, context, accept }: SignSurfaceProps): JSX.Element {
  const { t } = useTranslation();
  const [form, setForm] = useState<SignForm>(() => emptySignForm(context));
  const [errors, setErrors] = useState<SignFormErrors>({});
  // Owns the acceptance instant AND the Idempotency-Key for this signing, so a
  // double-click is ONE operation rather than two (QA-08-05-05). See
  // `sign-operation.service.ts`.
  const signOperation = useSignOperation();

  const set = <K extends keyof SignForm>(key: K, value: SignForm[K]): void => {
    // ⚠️ CHANGING THE TERMS ENDS THE OPERATION. A different start date or a
    // different consent set is a different acceptance, so it needs a new key — and
    // re-using the old key with a changed body is a `409 idempotency-key-conflict`,
    // not a replay.
    signOperation.reset();
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Attach or clear the drawn mark. Goes through the SAME operation reset as every
   * other term: a different drawing is a different acceptance, so re-using the old
   * idempotency key with a changed body would be a `409 idempotency-key-conflict`
   * rather than a replay. The bounds check itself lives in `sign.service.ts`.
   */
  const setSignature = (dataUri: string | null): void => {
    signOperation.reset();
    setForm((prev) => withDrawnSignature(prev, dataUri));
    // Clear a stale refusal from the error strip the moment the pad changes; the
    // next submit re-derives it from the form.
    setErrors((prev) => ({ ...prev, signature: undefined }));
  };

  const depositSatisfied = isDepositGateSatisfied(context.depositGate);

  // ⚠️ THE SAME QUERY THE DOCUMENT VIEWER READS (same key → one cache entry, one
  // request). The sign control is gated on the contract having been PRESENTED
  // (US-SF18.3), and sharing the query is how "can I press this?" and "what is on
  // screen?" stay one fact instead of two that can drift. See `ContractPreview`
  // for why this is not an `onPresentedChange` callback.
  const preview = useAgreementPreviewQuery({ candidateId, planId: slug });
  const gate = {
    documentPresented: preview.isSuccess,
    depositSatisfied,
    // The drawn mark is a GATE now, not a garnish. Derived from the form through
    // the service rather than read as `form.signatureImagePng !== undefined` here,
    // so a refused drawing cannot satisfy it — see `hasDrawnSignature`.
    signatureDrawn: hasDrawnSignature(form),
    submitting: accept.isPending,
  };
  const blockedReasonKey = signBlockedReasonKey(gate);
  const canSign = isSignActionable(gate);

  // The refusal the server explained, when it explained one customer-safely.
  const acceptDetail = customerSafeDetail(problemFieldsOf(accept.error));

  const handleAccept = (): void => {
    if (slug === undefined) return;
    // `undefined` means a submit is already in flight — the second click of a
    // double-click. Do nothing; the button's `disabled` cannot catch this on its
    // own because both clicks in one React tick read the same pre-commit state.
    const operation = signOperation.claim();
    if (operation === undefined) return;

    const result = buildSignatureEvent({
      form,
      context,
      candidateId,
      planId: slug,
      // The instant the customer accepted is the FIRST click, not this one — which
      // is both more truthful and what keeps the retried body byte-identical.
      now: operation.acceptedAt,
    });
    setErrors(result.errors);
    if (!result.ok || result.value === undefined) {
      signOperation.settle();
      return;
    }
    accept.mutate(
      { event: result.value, idempotencyKey: operation.idempotencyKey },
      { onSettled: () => signOperation.settle() },
    );
  };

  const submitError = accept.isError ? (
    <Alert
      variant={isNotImplemented(accept.error) ? 'warning' : 'danger'}
      title={t('shop.sign.submit.error.title')}
      role={isNotImplemented(accept.error) ? 'status' : 'alert'}
    >
      {/*
        E-SF19 P1 — the server's OWN actionable sentence, when it wrote one for the
        caller. The BFF returned "Complete the credit step before signing." and this
        alert rendered "Something went wrong", which, combined with the missing route
        gating, left the customer in a permanent dead end with nothing to act on.
        `customerSafeDetail` owns which details may be shown (4xx only, never a 5xx).
      */}
      {isNotImplemented(accept.error)
        ? t('shop.sign.error.notAvailable')
        : (acceptDetail ?? t('shop.sign.submit.error.body'))}
    </Alert>
  ) : null;

  return (
    // ── THE TWO COLUMNS ──────────────────────────────────────────────────────
    // `lg:grid-cols-[minmax(0,1fr)_26rem]` — the document takes the flexible half
    // and the action rail is a fixed, readable 416px. `minmax(0,1fr)` rather than a
    // bare `1fr` because a grid track's default `min-width: auto` refuses to shrink
    // below its content, which is how a wide PDF pushes the rail off screen.
    // Below `lg` this is a plain block flow, so the order below IS the mobile order.
    <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_26rem] lg:items-start lg:gap-8">
      {/* ── THE READING COLUMN ────────────────────────────────────────────────
          Sticky and viewport-tall on desktop, so the contract is still on screen
          while the customer works down the rail beside it — which is what "read the
          contract, then sign it" actually means. `h-[calc(100vh-6rem)]` leaves room
          for the sticky pill nav above; `top-24` parks it just below that nav. */}
      <div className="lg:sticky lg:top-24 lg:flex lg:h-[calc(100vh-8rem)] lg:flex-col">
        <ContractPreview
          candidateId={candidateId}
          planId={slug}
          publishEnabled={env.featureEflPublish}
          layout="column"
          textAlternative={
            <EflDisclosure
              eflText={context.disclosures.eflText}
              tosText={context.disclosures.tosText}
              yracText={context.disclosures.yracText}
              publishEnabled={env.featureEflPublish}
            />
          }
        />
      </div>

      {/* ── THE ACTION RAIL ──────────────────────────────────────────────────
          Everything the customer has to DO, in the order they have to do it, ending
          in the signing panel. Scrolls normally; on mobile it simply follows the
          document. */}
      <div className="flex flex-col gap-6">
        {/* ⚠️ NO START-DATE PICKER HERE — it moved to the ESTIMATE step
            (2026-09-18). Asking on this screen was asking a question whose
            answer was discarded: the DRAFT quote is created by the estimate
            step's "Continue to sign", and from then on its price and EFL are
            fixed and the accept path never re-prices, so a date chosen here
            could never reach the contract. It is now chosen before the quote
            exists, which is why the contract preview above renders it.
            See `buildStartDateSelection` before re-adding anything. */}

        {context.enrollmentType === 'SWITCH' ? <RescissionDisclosure /> : null}

        <ClickwrapConsent
          eflAccepted={form.eflAccepted}
          onEflAcceptedChange={(v) => set('eflAccepted', v)}
          tosAccepted={form.tosAccepted}
          onTosAcceptedChange={(v) => set('tosAccepted', v)}
          yracAccepted={form.yracAccepted}
          onYracAcceptedChange={(v) => set('yracAccepted', v)}
          tcpaSmsConsent={form.tcpaSmsConsent}
          onTcpaSmsConsentChange={(v) => set('tcpaSmsConsent', v)}
          errors={{ efl: errors.efl, tos: errors.tos, yrac: errors.yrac }}
        />

        <SignDepositGate candidateId={candidateId} gate={context.depositGate} />

        {/* THE END OF THE STEP. The drawn mark (optional — E-SF17) and the primary
            action, in one visually distinct panel. It stays last, as the customer
            asked; what changed is that arriving here now looks like arriving
            somewhere. */}
        <SigningPanel
          signaturePad={
            <SignaturePad
              onChange={setSignature}
              disabled={accept.isPending}
              // ⚠️ THE REFUSAL IS SHOWN AS SOON AS IT HAPPENS, not on submit.
              //
              // `errors.signature` alone was enough while the mark was optional: a
              // refused drawing left the button live, so pressing it produced the
              // explanation. Now a refused drawing leaves the button DISABLED, and
              // the standing reason beside it asks for a signature — which, to a
              // customer who has just drawn one, reads as the page ignoring them.
              // `form.signatureRejectedKey` is set the instant the pad refuses, so
              // the "that drawing couldn't be read" sentence lands under the pad
              // where their eye already is, and the disabled button is then
              // explained rather than mysterious.
              {...((form.signatureRejectedKey ?? errors.signature) === undefined
                ? {}
                : { errorKey: form.signatureRejectedKey ?? errors.signature })}
            />
          }
          canSign={canSign}
          blockedReasonKey={blockedReasonKey}
          consentIncomplete={!isConsentComplete(form)}
          submitting={accept.isPending}
          onSign={handleAccept}
          error={submitError}
        />
      </div>
    </div>
  );
}
